import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../config/firebase';
import { secureFetch } from '../../utils/secureFetch';
import UploadVideoModal from './UploadVideoModal';
import ProviderStories from './ProviderStories';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import './Reels.css';

const Reels = ({ onEmpty }) => {
  const [user] = useAuthState(auth);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showStories, setShowStories] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, processing, completed
  const [myStories, setMyStories] = useState([]);
  const fileInputRef = useRef(null);
  
  // Sistema de throttling global para coordinar todas las llamadas API
  const lastApiCallRef = useRef(0);
  const THROTTLE_DELAY = 2500; // 2.5 segundos entre cualquier llamada API



  const loadProviders = useCallback(async () => {
    try {
      // Throttling global: coordinar con otras llamadas API
      const now = Date.now();
      if (now - lastApiCallRef.current < THROTTLE_DELAY) {
        return; // Skip si muy reciente cualquier llamada
      }
      lastApiCallRef.current = now;
      
      const response = await fetch('https://api.minymol.com/reels/list?groupBy=provider&limit=10');

      if (!response.ok) {
        // Si es 404, significa que no hay reels disponibles
        if (response.status === 404) {
          setProviders([]);
          return;
        }
        // Si es 429, silenciar completamente
        if (response.status === 429) {
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Verificar que data sea un array válido
      if (Array.isArray(data)) {
        setProviders(data);
      } else {
        setProviders([]);
      }
    } catch (error) {
      // Silenciar completamente errores de rate limiting y de red
      if (!error.message?.includes('429') && !error.name?.includes('TypeError')) {
        console.error('Error cargando reels:', error);
      }
      setProviders([]); // Asegurar que providers sea siempre un array
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar mis historias si soy proveedor
  const loadMyStories = useCallback(async () => {
    if (!user) return;
    
    // Solo cargar si el usuario es proveedor
    const userData = localStorage.getItem('usuario');
    if (!userData) return;
    
    const usuario = JSON.parse(userData);
    if (usuario.rol !== 'proveedor') return;
    
    try {
      // Throttling global: coordinar con otras llamadas API + delay adicional
      const now = Date.now();
      if (now - lastApiCallRef.current < THROTTLE_DELAY) {
        // Si hay throttling activo, programar para después
        setTimeout(() => loadMyStories(), THROTTLE_DELAY);
        return;
      }
      lastApiCallRef.current = now;
      
      const userToken = await user.getIdToken();
      const response = await secureFetch('https://api.minymol.com/reels/my-reels', {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMyStories(data);
      } else if (response.status === 429) {
        // Silenciar rate limiting completamente
        return;
      }
    } catch (error) {
      // Silenciar completamente errores de rate limiting y de red
      if (!error.message?.includes('429') && !error.name?.includes('TypeError')) {
        console.error('Error cargando mis historias:', error);
      }
      setMyStories([]);
    }
  }, [user]);

  // Cargar providers con reels
  useEffect(() => {
    // Cargar providers inmediatamente
    loadProviders();
    
    // Si hay usuario, cargar mis historias con delay para evitar solapamiento
    if (user) {
      setTimeout(() => loadMyStories(), THROTTLE_DELAY + 500);
    }
  }, [loadProviders, loadMyStories, user]);

  // Manejar upload de video
  const handleVideoUpload = async (file) => {
    if (!file || !user) return;

    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);

    try {
      // Obtener duración del video
      const duration = await getVideoDuration(file);

      if (duration > 60) {
        alert('El video no puede ser mayor a 60 segundos');
        return;
      }

      setUploadProgress(10);

      // 1. Solicitar presigned URL
      const uploadRequest = await secureFetch('https://api.minymol.com/reels/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          duration: Math.round(duration),
          contentType: file.type
        })
      });

      if (!uploadRequest.ok) {
        const error = await uploadRequest.json();
        throw new Error(error.message || 'Error al solicitar upload');
      }

      const { presignedUrl, uploadKey } = await uploadRequest.json();
      setUploadProgress(30);

      // 2. Subir a S3 con progreso
      const xhr = new XMLHttpRequest();
      
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = 30 + Math.round((e.loaded / e.total) * 50);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            setUploadProgress(80);
            resolve();
          } else {
            reject(new Error('Error al subir video a S3'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Error de red durante el upload'));
        });

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      await uploadPromise;

      // 3. Notificar completado
      await secureFetch('https://api.minymol.com/reels/upload-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadKey })
      });

      setUploadProgress(100);
      setUploadStatus('processing');
      
      // Simular progreso de procesamiento
      let processing = 0;
      const processingInterval = setInterval(() => {
        processing += Math.random() * 15;
        if (processing >= 100) {
          processing = 100;
          clearInterval(processingInterval);
          setUploadStatus('completed');
          
          // Recargar datos después del procesamiento
          setTimeout(() => {
            loadProviders();
            loadMyStories();
            setShowUploadModal(false);
          }, 2000);
        }
        setProcessingProgress(processing);
      }, 500);

    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Error: ' + error.message);
      setUploadStatus('error');
    } finally {
      setUploading(false);
    }
  };

  // Eliminar un reel
  const handleDeleteReel = async (reelId) => {
    if (!user) return;

    try {
      const userToken = await user.getIdToken();
      const response = await secureFetch(`https://api.minymol.com/reels/${reelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        // Actualizar la lista de mis historias
        setMyStories(prev => prev.filter(reel => reel.id !== reelId));
        
        // Si estamos viendo las historias y eliminamos el reel actual, cerrar el modal
        if (selectedProvider && selectedProvider.provider.id === user.uid) {
          const updatedReels = selectedProvider.reels.filter(reel => reel.id !== reelId);
          if (updatedReels.length === 0) {
            setShowStories(false);
            setSelectedProvider(null);
          } else {
            // Actualizar la lista de reels en el provider seleccionado
            setSelectedProvider(prev => ({
              ...prev,
              reels: updatedReels,
              totalReels: updatedReels.length
            }));
          }
        }

        // Recargar providers para actualizar el feed principal
        loadProviders();
      } else {
        throw new Error('Error al eliminar el reel');
      }
    } catch (error) {
      console.error('Error eliminando reel:', error);
      alert('Error al eliminar el reel. Intenta nuevamente.');
    }
  };



  // Obtener duración del video
  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });
  };



  const openProviderStories = (provider) => {
    setSelectedProvider(provider);
    setShowStories(true);
  };

  const openMyStories = () => {
    if (myStories.length > 0) {
      // Usar el provider ID del primer reel (todos deberían tener el mismo provider)
      const firstReel = myStories[0];
      const myProvider = {
        provider: {
          id: firstReel.provider?.id || user.uid,
          nombre_empresa: firstReel.provider?.nombre_empresa || 'Mis historias',
          logo_url: firstReel.provider?.logo_url || user.photoURL || '/logo192.png'
        },
        reels: myStories,
        totalReels: myStories.length
      };
      setSelectedProvider(myProvider);
      setShowStories(true);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleVideoUpload(file);
    }
  };

  if (loading) {
    return (
      <div className="reels-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Verificar si el usuario es proveedor
  const isProvider = user && localStorage.getItem('usuario') && 
    JSON.parse(localStorage.getItem('usuario'))?.proveedorInfo?.id;

  // No mostrar nada si no hay reels y el usuario no es proveedor
  if ((!providers || providers.length === 0) && (!user || !isProvider)) {
    // Llamar al callback para indicar que no hay contenido
    if (onEmpty) {
      onEmpty();
    }
    return null;
  }

  return (
    <div className="reels-container">
      <div className="reels-header">
        <h3>Historias</h3>
      </div>

      <div className="reels-scroll-container">
        {/* Botón de agregar historia (separado) */}
        {user && (
          <div className="reel-item">
            <div
              className="reels-thumbnail reels-add-new"
              onClick={() => setShowUploadModal(true)}
            >
              <div className="reels-upload-placeholder">
                <div className="reels-upload-circle">
                  <div className="reels-plus-icon">
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                </div>
              </div>
            </div>
            <span className="reel-provider-name">Agregar historia</span>
          </div>
        )}

        {/* Mis historias (solo si tengo historias) */}
        {user && myStories.length > 0 && (
          <div className="reel-item">
            <div
              className="reels-thumbnail"
              onClick={() => openMyStories()}
            >
              <img
                src={myStories[0]?.thumbnailUrl || '/logo192.png'}
                alt="Mis historias"
                onError={(e) => {
                  // Si falla el thumbnail, usar el logo del provider
                  e.target.src = myStories[0]?.provider?.logo_url || '/logo192.png';
                  e.target.classList.add('reels-fallback-logo');
                }}
              />
              <div className="reels-provider-logo">
                <img
                  src={myStories[0]?.provider?.logo_url || '/logo192.png'}
                  alt="Logo"
                  onError={(e) => {
                    e.target.src = '/logo192.png';
                  }}
                />
              </div>
              <div className="reels-count">
                {myStories.length}
              </div>
            </div>
            <span className="reel-provider-name">Mis historias</span>
          </div>
        )}

        {/* Lista de providers con reels (excluyendo mis historias) */}
        {Array.isArray(providers) && providers.length > 0 ? (
          providers
            .filter((providerData) => {
              // Excluir mis historias del listado general
              if (!user || !myStories.length > 0) return true;
              return providerData.provider.id !== myStories[0]?.provider?.id;
            })
            .map((providerData) => (
            <div
              key={providerData.provider.id}
              className="reel-item"
              onClick={() => openProviderStories(providerData)}
            >
              <div className="reels-thumbnail">
                <img
                  src={providerData.reels[0]?.thumbnailUrl || '/logo.webp'}
                  alt={providerData.provider.nombre_empresa}
                  onError={(e) => {
                    // Si falla el thumbnail, usar el logo del provider directamente
                    e.target.src = providerData.provider.logo_url || '/logo.webp';
                    e.target.classList.add('reels-fallback-logo');
                  }}
                />
                <div className="reels-provider-logo">
                  <img
                    src={providerData.provider.logo_url || '/logo.webp'}
                    alt="Logo"
                    onError={(e) => {
                      e.target.src = '/logo.webp';
                    }}
                  />
                </div>
                <div className="reels-count">
                  {providerData.totalReels}
                </div>
              </div>
              <span className="reel-provider-name">
                {providerData.provider.nombre_empresa}
              </span>
            </div>
            ))
        ) : (
          !user && (
            <div className="no-reels-message">
              <p>No hay historias disponibles aún</p>
            </div>
          )
        )}
      </div>

      {/* Input oculto para seleccionar archivos */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Modal de upload */}
      {showUploadModal && (
        <UploadVideoModal
          onClose={() => {
            setShowUploadModal(false);
            setUploadStatus('idle');
            setUploadProgress(0);
            setProcessingProgress(0);
          }}
          onUpload={handleVideoUpload}
          uploading={uploading}
          uploadProgress={uploadProgress}
          processingProgress={processingProgress}
          uploadStatus={uploadStatus}
        />
      )}

      {/* Modal de historias */}
      {showStories && selectedProvider && (
        <ProviderStories
          provider={selectedProvider}
          onClose={() => {
            setShowStories(false);
            setSelectedProvider(null);
          }}
          isOwner={(() => {
            const usuario = localStorage.getItem('usuario');
            return user && selectedProvider.provider.id === JSON.parse(usuario)?.proveedorInfo?.id;
          })()}
          onDeleteReel={(reel) => handleDeleteReel(reel.id)}
        />
      )}
    </div>
  );
};

export default Reels;
